import { LinkBreak } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components';
import { useAgGridServerTableContext, useNotificationContext } from 'core/states';
import { useProjectActions } from 'projects/hooks';
import { Color, Variant } from 'theme/enums';

import { DetachConfirmation } from './DetachConfirmation';

interface IProps {
  projectId: number;
}

export const DetachQuoteButton = ({ projectId }: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const { t } = useTranslation();

  const { isLoadingTopNotificationDisplayed } = useNotificationContext();
  const { selectedRows, deselectAllRows, updateRows } = useAgGridServerTableContext();

  const { detachQuotesFromProject } = useProjectActions();

  const onlyAttachedRecordsSelected = useMemo(() => {
    if (!selectedRows?.length) {
      return false;
    }

    return selectedRows.every((quote) => quote.customerProjectId !== projectId);
  }, [selectedRows, projectId]);

  const onSubmit = async () => {
    setIsConfirmationOpen(false);

    const { success } = await detachQuotesFromProject({
      projectQuoteAssociations: [
        {
          projectId,
          quoteId: selectedRows.map((quote) => quote.id),
        },
      ],
    });

    if (!success) {
      return;
    }

    handleOnSuccess();
  };

  const handleOnSuccess = () => {
    deselectAllRows();

    updateRows({ remove: selectedRows });
  };

  return (
    <>
      <Button
        text={t('buttonLabelDetach')}
        color={Color.DANGER}
        variant={Variant.SOFT}
        startDecorator={<LinkBreak />}
        onClick={() => setIsConfirmationOpen(true)}
        disabled={
          isLoadingTopNotificationDisplayed || !selectedRows?.length || !onlyAttachedRecordsSelected
        }
      />

      <DetachConfirmation
        isOpen={isConfirmationOpen}
        onConfirm={onSubmit}
        onCancel={() => setIsConfirmationOpen(false)}
        selectionCount={selectedRows?.length || 0}
      />
    </>
  );
};
